// React imports
import { useState, useEffect, useMemo } from 'react';

// Other imports
import axios from 'axios';
import _ from 'lodash';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { portKey } from '../dashboard/rates/portGroups.js';

// Older versions of rates about to be published. Suppliers send a new version of a file before
// the old one expires, so rates already in the workspace with the same supplier, product, mode,
// route and transshipment, valid at the same time as the new ones, are older versions. The
// reviewer chooses to end them the day before the new rates start, delete them, or keep both;
// unpublishing puts them back. Shared by both review screens.

// A port can have several codes (portGroups.js): the key uses one code per port.
const placeKey = key => { const [type, id] = String(key || '').split(':'); return `${type || ''}:${portKey(id)}`; };
const rateKey = r => [String(r.supplier || '').trim().toLowerCase(), r.productType, r.dangerous ? 'dg' : '', r.transportationMethod, placeKey(r.sourceKey), placeKey(r.destinationKey), r.transshipment || ''].join('|');

function describe(rate) {
    return {
        supplier: rate.supplier?.organization, productType: rate.product?.type, dangerous: !!rate.product?.dangerous,
        transportationMethod: rate.transportationMethod,
        sourceKey: `${rate.source?.type || ''}:${rate.source?.id || ''}`, destinationKey: `${rate.destination?.type || ''}:${rate.destination?.id || ''}`,
        transshipment: rate.offer?.transshipment || '', validFrom: rate.offer?.validFrom || null, validUntil: rate.offer?.validUntil || null,
        lane: { transportationMethod: rate.transportationMethod, sourceType: rate.source?.type || '', source: rate.source?.id || '', destinationType: rate.destination?.type || '', destination: rate.destination?.id || '' },
    };
}

// `rates`: the rate objects that would be published. `ownIds`: rates this run already published,
// which are not older versions of themselves.
export function useOlderRates({ org, accesstoken, rates, ownIds = [] }) {
    const [ existing, setExisting ] = useState(null);
    const [ action, setAction ] = useState('end');
    const described = useMemo(() => (rates || []).map(describe), [rates]);
    const lanesKey = JSON.stringify(_.uniqBy(described.map(r => r.lane), l => JSON.stringify(l)));

    useEffect(() => {
        const lanes = JSON.parse(lanesKey);
        if (!lanes.length) { setExisting([]); return undefined; }
        let cancelled = false;
        setExisting(null);
        axios.post(`/api/admin/elariom/${org.id}/existing-rates?accesstoken=${accesstoken}`, { lanes })
            .then(({ data }) => !cancelled && setExisting(data))
            .catch(() => !cancelled && setExisting([]));
        return () => { cancelled = true; };
    }, [lanesKey, org.id, accesstoken]);

    const ownKey = (ownIds || []).join(',');
    const replaced = useMemo(() => {
        if (!existing) return [];
        const own = new Set(ownKey ? ownKey.split(',') : []);
        const groups = _.groupBy(described, rateKey);
        return existing.filter(e => !own.has(e.id)).map(e => {
            const group = groups[rateKey(e)];
            if (!group) return null;
            const from = _.min(group.map(g => g.validFrom).filter(Boolean));
            const until = group.some(g => !g.validUntil) ? null : _.max(group.map(g => g.validUntil));
            const overlaps = (!until || !e.validFrom || e.validFrom <= until) && (!from || !e.validUntil || e.validUntil >= from);
            if (!overlaps) return null;
            return { ...e, newFrom: from, endOn: from ? dayjs(from).subtract(1, 'day').format('YYYY-MM-DD') : null };
        }).filter(Boolean);
    }, [existing, described, ownKey]);

    // After the new rates are created: apply the choice and return what to record with the publish.
    const apply = async () => {
        if (!replaced.length) return null;
        if (action === 'keep') return { action: 'keep', count: replaced.length, ids: replaced.map(e => e.id) };
        const { data } = await axios.post(`/api/admin/elariom/${org.id}/existing-rates/apply?accesstoken=${accesstoken}`, {
            action,
            items: replaced.map(e => ({ id: e.id, validUntil: e.endOn })),
        });
        return { action, count: replaced.length, ended: data.ended || [], deleted: data.deleted || [] };
    };

    return { replaced, loading: existing === null, action, setAction, apply };
}

// On unpublish: put the older rates back as they were.
export async function restoreOlderRates({ org, accesstoken, record }) {
    if (!record || !['end', 'delete'].includes(record.action)) return;
    if (!(record.ended || []).length && !(record.deleted || []).length) return;
    await axios.post(`/api/admin/elariom/${org.id}/existing-rates/apply?accesstoken=${accesstoken}`, { action: 'restore', ended: record.ended || [], deleted: record.deleted || [] });
}

export function OlderRatesPanel({ older, workspace }) {
    const theme = useTheme();
    const [ show, setShow ] = useState(false);
    const { replaced, action, setAction } = older;
    if (!replaced.length) return null;
    return (
        <Box sx={{ border: `1px solid ${theme.palette.info.main}`, borderRadius: '8px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {replaced.length} rate{replaced.length === 1 ? '' : 's'} already in {workspace} look{replaced.length === 1 ? 's' : ''} like an older version of {replaced.length === 1 ? 'one' : 'some'} of these
                </Typography>
                <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, marginTop: '2px', maxWidth: '760px' }}>
                    They have the same supplier, product, route and transshipment as rates in this file, and they are valid at the same time. Choose what should happen to them when you publish.
                </Typography>
            </Box>
            {[
                { value: 'end', title: 'End them the day before the new rates start (recommended)', text: `Each older rate stays in Monada for history, but stops being valid the day before its new version starts${replaced[0]?.endOn ? `, e.g. on ${dayjs(replaced[0].endOn).format('D MMM YYYY')}` : ''}. Rate search and quoting then use only the new rates.` },
                { value: 'delete', title: 'Delete them', text: 'The older rates are removed from Monada. Quotes already priced with them keep their prices.' },
                { value: 'keep', title: 'Keep them as they are', text: 'Both versions stay valid at the same time, and rate search shows both.' },
            ].map(o => (
                <Box key={o.value} component='label' sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px' }}>
                    <input type='radio' name='replace-action' value={o.value} checked={action === o.value} onChange={() => setAction(o.value)} style={{ marginTop: '3px' }} />
                    <span><b>{o.title}</b><br /><span style={{ color: theme.palette.text.secondary }}>{o.text}</span></span>
                </Box>
            ))}
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                If you unpublish this file later, the older rates are put back exactly as they were.
            </Typography>
            <Box>
                <Button size='small' onClick={() => setShow(v => !v)} sx={{ textTransform: 'none', fontSize: '12px', padding: '0 4px' }}>
                    {show ? 'Hide the older rates' : `Show the ${replaced.length} older rate${replaced.length === 1 ? '' : 's'}`}
                </Button>
                {show && (
                    <Box sx={{ maxHeight: '220px', overflow: 'auto', marginTop: '6px', fontSize: '12px', border: `1px solid ${theme.palette.border1}`, borderRadius: '6px' }}>
                        <Box component='table' sx={{ borderCollapse: 'collapse', width: '100%', '& td, & th': { padding: '3px 8px', borderBottom: `1px solid ${theme.palette.border1}`, textAlign: 'left', whiteSpace: 'nowrap' } }}>
                            <thead><tr><th>From</th><th>To</th><th>Product</th><th>Supplier</th><th>Valid now</th><th>Would end on</th></tr></thead>
                            <tbody>
                                {replaced.slice(0, 500).map(e => (
                                    <tr key={e.id}>
                                        <td>{e.sourceText}</td><td>{e.destinationText}</td><td>{e.productType}</td><td>{e.supplier}</td>
                                        <td>{e.validFrom || '…'} → {e.validUntil || 'no end'}</td>
                                        <td>{e.endOn || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export function olderRatesSentence({ replaced, action }) {
    if (!replaced.length || action === 'keep') return 'No existing rates are changed. ';
    return action === 'end'
        ? `${replaced.length} older version${replaced.length === 1 ? '' : 's'} of these rates will stop being valid the day before the new ones start. `
        : `${replaced.length} older version${replaced.length === 1 ? '' : 's'} of these rates will be deleted. `;
}
